import {
  AgxService,
  useGetPoisonQueues,
  useGetPoisonQueueMessages,
} from '@urbanx/agx.support.client';
import {
  AgxCheckbox,
  AgxRow,
  AgxTextInput,
  AgxColumn,
  AgxTable,
  AgxTypography,
  IColumnConfig,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SupportBackButton from '../SupportBackButton';
import { messageRows } from '../PoisonQueue/messageRows';
import {
  filterMessages,
  messageFilterLabels,
  type MessageFilters,
} from '../PoisonQueue/messageFilters';

const tableColumns: (string | IColumnConfig)[] = [
  'Service',
  'Queue',
  { name: 'Number of Messages', align: 'right' },
];
function QueueSearchResults({
  service,
  queueName,
  filters,
}: {
  service: AgxService;
  queueName: string;
  filters: MessageFilters;
}) {
  const { data, isPending, isError } = useGetPoisonQueueMessages(service, queueName);
  const matches = filterMessages(data ?? [], filters);

  if (!isPending && !isError && matches.length === 0) return null;

  return (
    <AgxColumn fill gap="spacing-xl" alignItems="stretch">
      <AgxTypography type="title" size="large" textAlign="left">
        {service} — {queueName}
      </AgxTypography>
      {isError ? (
        <div role="alert">
          Could not search this queue. Results are incomplete; clear and re-enter a filter to retry.
        </div>
      ) : (
        <>
          <AgxTable
            columns={['Id', 'Inserted At', 'Message Body']}
            rows={messageRows(matches)}
            showLoadingState={isPending}
            noDataLabel="No matching messages in the first 32 visible messages"
            renderRowLink={(index, linkProps) => {
              const message = matches[index];
              if (!message?.id) return;
              return (
                <Link
                  {...linkProps}
                  to={`./${encodeURIComponent(service)}/${encodeURIComponent(queueName)}/${encodeURIComponent(message.id)}`}
                  aria-label={message.id}
                />
              );
            }}
          />
        </>
      )}
    </AgxColumn>
  );
}

const PoisonQueues = () => {
  const [filters, setFilters] = useState<MessageFilters>({
    campaignId: '',
    contractId: '',
    eventType: '',
    userId: '',
  });
  const hasFilters = Object.values(filters).some((value) => value.trim());
  const {
    data: poisonQueues,
    isPending: isPoisonQueuesPending,
    isError: isPoisonQueuesError,
  } = useGetPoisonQueues();

  const [filterToQueuesWithMessages, setFilterToQueuesWithMessages] = useState(true);

  const filteredPoisonQueues = useMemo(() => {
    return (
      (poisonQueues ?? [])
        .flatMap((pq) =>
          (pq.poisonQueues ?? []).map((queue) => ({
            service: pq.agxService,
            queue: queue.name,
            itemCount: queue.itemCount,
          }))
        )
        .filter(
          (queue) => !filterToQueuesWithMessages || (queue.itemCount && queue.itemCount > 0)
        ) ?? []
    );
  }, [poisonQueues, filterToQueuesWithMessages]);

  const rows = useMemo(() => {
    if (!filteredPoisonQueues) return [];

    return (
      filteredPoisonQueues.map((pq, idx) => ({
        Service: (
          <AgxTypography id={`poison-queue-${idx}-service`} type={'body_ui'} size={'small'}>
            {pq.service?.toString()}
          </AgxTypography>
        ),
        Queue: (
          <AgxTypography id={`poison-queue-${idx}-queue`} type={'body_ui'} size={'small'}>
            {pq.queue}
          </AgxTypography>
        ),
        'Number of Messages': (
          <AgxTypography id={`poison-queue-${idx}-queue`} type={'body_ui'} size={'small'}>
            {pq.itemCount}
          </AgxTypography>
        ),
      })) ?? []
    );
  }, [filteredPoisonQueues]);

  return (
    <AgxColumn gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel title="Queues with errors">
        <AgxRow gap="spacing-sm" wrap>
          {Object.entries(messageFilterLabels).map(([key, label]) => (
            <AgxTextInput
              key={key}
              id={`poison-queues-filter-${key}`}
              type="text"
              label={label}
              hideOptionalText
              onChange={(value) => setFilters((previous) => ({ ...previous, [key]: value }))}
            />
          ))}
        </AgxRow>
        {isPoisonQueuesError && (
          <div role="alert">Could not load queues. Refresh the page to retry.</div>
        )}
        <AgxCheckbox
          id="filterPoisonQueuesCheckbox"
          checked={filterToQueuesWithMessages}
          onChange={() => setFilterToQueuesWithMessages(!filterToQueuesWithMessages)}
          label="Show only queues with messages"
        />
        {hasFilters ? (
          <AgxColumn fill gap="spacing-xl" padding={{ top: 'spacing-xl' }}>
            {isPoisonQueuesPending && <div role="status">Loading queues…</div>}
            {!isPoisonQueuesPending &&
              !isPoisonQueuesError &&
              filteredPoisonQueues.length === 0 && <div role="status">No queues to search.</div>}
            {filteredPoisonQueues.map((queue) =>
              queue.service && queue.queue ? (
                <QueueSearchResults
                  key={`${queue.service}/${queue.queue}`}
                  service={queue.service}
                  queueName={queue.queue}
                  filters={filters}
                />
              ) : null
            )}
          </AgxColumn>
        ) : (
          <AgxTable
            columns={tableColumns}
            rows={rows}
            renderRowLink={(index, linkProps) => {
              const queue = filteredPoisonQueues[index];
              if (!queue?.service || !queue.queue) return;
              return (
                <Link
                  {...linkProps}
                  to={`./${encodeURIComponent(queue.service.toString())}/${encodeURIComponent(queue.queue)}`}
                  aria-label={`${queue.service} ${queue.queue}`}
                />
              );
            }}
            showLoadingState={isPoisonQueuesPending}
          />
        )}
      </Panel>
    </AgxColumn>
  );
};

export default PoisonQueues;
